import { useState, useEffect, useRef } from "react";
// HELPERS
import api from "@helpers/api";
import i18n from "@helpers/i18n";
import { formatSSN, logUserAction } from "@helpers/utils";

// HOOKS
// import useResponsive from "@hooks/useResponsive";

const useUsersTable = props => {
  const { user, configOptions } = props;
  const [loaded, setLoaded] = useState(false);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("first_name");
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [page, setPage] = useState(0);
  const [users, setUsers] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [resellers, setResellers] = useState([]);
  const [copyList, setCopyList] = useState([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [userStatusModalOpen, setUserStatusModalOpen] = useState(false);
  const [newUserAdded, setNewUserAdded] = useState(null);
  const [activeTab, setActiveTab] = useState("1");
  const searchInput = useRef(null);
  const filters = useRef({
    tabKey: "1",
    roleId: false,
    searchString: ""
  });

  let columns = [];
  columns.push(
    {
      header: "",
      id: "menuButton",
      accessor: "menuButton",
      width: "2.5%",
      disablePadding: true,
      disableSort: true
    },
    {
      header: "Roll",
      accessor: "role_id",
      width: "10%"
    },
    {
      header: "Personnr",
      accessor: "personal_number",
      width: "12.5%"
    },
    {
      header: "Förnamn",
      accessor: "first_name",
      width: "7.5%"
    },
    {
      header: "Efternamn",
      accessor: "last_name",
      width: "10%"
    },
    {
      header: "Kontaktuppgifter",
      accessor: "email",
      width: "30%"
    },
    {
      header: "Företag",
      accessor: "reseller_id",
      width: "20%"
    },
    {
      header: "Senaste aktivitet",
      accessor: "last_action_timestamp",
      width: "10%"
    }
  );

  const roles = {
    all: 0,
    administrator: 1,
    backoffice: 2,
    advisor: 3,
    sales: 4,
    assistant: 5
  };

  const tabs = [
    {
      key: "1",
      label: "Aktiva"
    },
    {
      key: "0",
      label: "Inaktiverade"
    }
  ];

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setPage(0);
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const updateUsers = user => {
    resetFilters();

    // Create a lookup map for faster user lookup
    const usersMap = Object.fromEntries(
      users.map(user => [user.personal_number, user])
    );    

    // check if user has advisors and map them to advisors_assisted prop containing personal_number, first_name, last_name
    if (Array.isArray(user.advisor_personal_numbers) && user.advisor_personal_numbers.length > 0) {
      user.showAllAdvisors = false;
      user.advisors_assisted = user.advisor_personal_numbers.map(personalNumber => usersMap[personalNumber]).filter(Boolean).map(advisor => ({
        personal_number: advisor.personal_number,
        first_name: advisor.first_name,
        last_name: advisor.last_name
      })).sort((a, b) => a.first_name.localeCompare(b.first_name));
    }
    

    // Find existing index
    let index = users.findIndex(
      u =>
        (u.personal_number &&
          user.personalNumber &&
          u.personal_number === user.personalNumber) ||
        (u.personalNumber &&
          user.personalNumber &&
          u.personalNumber === user.personalNumber) ||
        (u.personal_number &&
          user.personal_number &&
          u.personal_number === user.personal_number)
    );

    const isNewUser = index === -1;
    // if not found, add to end (new user)
    if (isNewUser) {
      index = users.length;
      setNewUserAdded(user);

      setTimeout(() => {
        setNewUserAdded(null);
      }, 3000);
    }
    // Update users with new user record
    users[index] = user;
    // Sort alphabetically
    users.sort((a, b) =>
      (a.first_name || a.firstName).localeCompare(
        b.first_name || b.firstName,
        "sv",
        { sensitivity: "base" }
      )
    );

    return setCopyList(users.filter(user => user.is_active == activeTab));
  };

  const resetFilters = () => {
    setPage(0);
    filters.current = {
      ...filters.current,
      roleId: null,
      search: ""
    };
    searchInput.current.value = "";
    setIsFiltered(false);
    applyFilters();
  };

  const handleTabChange = newTab => {
    filters.current.tabKey = newTab;
    applyFilters();
  };

  const handleRoleChange = event => {
    filters.current.roleId = event.target.value || null;
    applyFilters();
  };

  const requestSearch = searched => {
    filters.current.search = searched;
    applyFilters();
  };

  const applyFilters = () => {
    let filteredData = users;
    let filtered = false;

    if (filters.current.tabKey) {
      filteredData = filteredData.filter(
        user => user.is_active == filters.current.tabKey
      );
      setActiveTab(filters.current.tabKey);
    }

    if (filters.current.roleId) {
      filtered = true;
      filteredData = filteredData.filter(
        user => user.role_id === filters.current.roleId
      );
    }

    if (filters.current.search) {
      filtered = true;
      const searchString = filters.current.search.toLowerCase();
      filteredData = filteredData.filter(user => {
        const userRoleLabel = i18n(
          "user_roles",
          Object.keys(roles).find(role => roles[role] == user.role_id)
        );
        const userResellerLabel = resellers.find(
          reseller => reseller.reseller_id == user.reseller_id
        )?.company_name;
        const personalNumWithDash = formatSSN(user.personal_number, {
          withDash: true
        });

        return (
          user.is_active === activeTab &&
          ((user.first_name + " " + user.last_name)
            .toLowerCase()
            .includes(searchString) ||
            user.personal_number?.toLowerCase().includes(searchString) ||
            personalNumWithDash?.toLowerCase().includes(searchString) ||
            (userRoleLabel &&
              userRoleLabel.toLowerCase().includes(searchString)) ||
            (userResellerLabel &&
              userResellerLabel.toLowerCase().includes(searchString)))
        );
      });
    }

    if (page > 0) setPage(0);
    setIsFiltered(filtered);
    setCopyList(filteredData);
  };

  const toggleAddUserModal = action => {
    if (action === "add") setSelectedUser(null);

    if (addUserModalOpen) {
      // disable/enable background content scrolling while modal is open
      document.documentElement.style.overflow = "auto";
    } else {
      document.documentElement.style.overflow = "hidden";
    }

    setAddUserModalOpen(!addUserModalOpen);
  };

  const toggleUserStatusModal = () => {
    if (userStatusModalOpen) {
      // disable/enable background content scrolling while modal is open
      document.documentElement.style.overflow = "auto";
    } else {
      document.documentElement.style.overflow = "hidden";
    }

    setUserStatusModalOpen(!userStatusModalOpen);
  };

  const toggleShowMoreAdvisors = user => {
    // Find user in copyList and set showAllAdvisors to true/false
    const foundUser = copyList.find(u => u.personal_number === user);
    if (foundUser) {
      foundUser.showAllAdvisors = !foundUser.showAllAdvisors;
      setCopyList([...copyList]);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      const { minActivityLogTime } = configOptions;
      const response = await api("partner/users", {
        minActivityLogTime
      }, {}, true);

      const { success, result } = response;
      if (success && result) {

        // Create a lookup map for faster advisor lookup
        const userMap = Object.fromEntries(
          result.map(user => [user.personal_number, user])
        );

        result.forEach(user => {
          // check which users have advisors and map them
          if (Array.isArray(user.advisor_personal_numbers) && user.advisor_personal_numbers.length > 0) {
            user.advisors_assisted = user.advisor_personal_numbers
              .map(personalNumber => userMap[personalNumber])
              .filter(Boolean)
              .map(advisor => ({
                // filter out unnecessary fields
                personal_number: advisor.personal_number,
                first_name: advisor.first_name,
                last_name: advisor.last_name
              }))
              // sort alphabetically
              .sort((a, b) => a.first_name.localeCompare(b.first_name));
          }
        });
        setUsers(result);
        // Set Advisors
        setAdvisors(
          result.filter(
            user =>
              user.role_id === "3" &&
              user.is_active == "1"
          )
        );
        // Get unique roles from result
        const uniqueRoles = Array.from(
          new Set(result.map(user => user.role_id))
        );
        // Map roles to userRoles
        setUserRoles(
          uniqueRoles.map(role => {
            return {
              id: role,
              label: i18n(
                "user_roles",
                Object.keys(roles).find(key => roles[key] == role)
              )
            };
          })
        );
        setCopyList(result.filter(user => user.is_active == activeTab));
      }
      setLoaded(true);
    };

    const fetchResellers = async () => {
      const response = await api("/partner/resellers", {}, {}, true);
      const { success, result } = response;
      if (success && result) {
        setResellers(result);
      }
    };

    if (!loaded) {
      fetchUsers();
      fetchResellers();
      logUserAction({
        action: "NAV_USERS_VIEW",
      });
    }
  }, [loaded]);

  return {
    loaded,
    mainUser: user,
    configOptions,
    columns,
    roles,
    userRoles,
    tabs,
    activeTab,
    isFiltered,
    filters,
    advisors,
    copyList,
    resellers,
    page,
    order,
    orderBy,
    rowsPerPage,
    selectedUser,
    searchInput,
    setPage,
    setSelectedUser,
    setRowsPerPage,
    handleTabChange,
    handleRoleChange,
    handleChangePage,
    handleRequestSort,
    requestSearch,
    toggleAddUserModal,
    toggleUserStatusModal,
    toggleShowMoreAdvisors,
    updateUsers,
    resetFilters,
    addUserModalOpen,
    userStatusModalOpen,
    newUserAdded
  };
};

export default useUsersTable;
